<template>
  <div>
    <div class="text-muted">
      {{
        $t('features.long_term.step.description', {
          meter_count: meterCount,
          peak_watt_sum: sumPeakWatt,
        })
      }}
    </div>

    <Divider />

    <div
      v-for="ltmProposal in ltmProposals"
      :key="ltmProposal.title"
      class="flex my-2"
    >
      <RadioButton
        v-model="selectedProposalTitle"
        :value="ltmProposal.title"
        class="self-center mr-3"
      />

      <Message :closable="false">
        <div class="text-xl">
          {{ titleForProposal(ltmProposal) }}
        </div>
        {{ ltmProposal.description }}
      </Message>
    </div>

    <div class="flex flex-row">
      <RadioButton
        v-model="selectedProposalTitle"
        :value="customProposalTitle"
        class="self-center mr-3"
      />

      <Message :closable="false">
        <div class="text-xl">{{ customProposalTitle }}</div>
        {{ customProposalDescription }}
        <div class="flex align-items-center">
          <InputGroup class="mt-2" style="width: 16rem">
            <InputNumber
              v-model="customEnergy"
              :disabled="selectedProposalTitle !== customProposalTitle"
            />
            <InputGroupAddon>{{ energyUnit }}</InputGroupAddon>
          </InputGroup>
        </div>
      </Message>
    </div>

    <Skeleton v-if="proposalsLoading" width="96%" height="20rem" class="ml-8" />

    <Divider />

    <div class="flex float-right">
      <Button
        class="mr-3"
        severity="secondary"
        @click="$emit('cancel-clicked')"
      >
        {{ $t('global.cancel') }}
      </Button>

      <Button
        @click="updatePeriod"
        :disabled="saveDisabled"
        variant="primary"
        :loading="saveOngoing"
      >
        {{ $t('global.save') }}
      </Button>
    </div>
  </div>
</template>
<script lang="ts">
import PlantApi from '@/data/plants/PlantApi.ts';
import PrognosisApi from '@/data/long-term/PrognosisApi.ts';
import YieldPromiseStatusApi from '@/data/yields/YieldPromiseStatusApi.ts';
import Company from '@/domain/model/business/Company.ts';
import Meter from '@/domain/model/business/Meter.ts';
import Plant from '@/domain/model/business/Plant.ts';
import YieldPromiseStatus from '@/domain/model/business/YieldPromiseStatus.ts';
import Energy from '@/domain/model/unit/Energy.ts';

export default {
  name: 'LongTermPeriodSettingsHelper',
  props: {
    plant: { type: Plant, required: true },
    company: { type: Company, required: true },
    period: { type: YieldPromiseStatus, required: true },
  },
  emits: ['period-updated', 'cancel-clicked'],
  methods: {
    titleForProposal(ltmProposal): string {
      return (
        ltmProposal.title +
        ': ' +
        ltmProposal.energy.asStringOfUnit(this.energyUnit) +
        ' ' +
        this.energyUnit
      );
    },
    updatePeriod(): void {
      this.saveOngoing = true;
      let newPromiseTotalEnergy: Energy;

      switch (this.selectedProposalTitle) {
        case this.proposalOptimistic.title:
          newPromiseTotalEnergy = this.proposalOptimistic.energy;
          break;
        case this.proposalConservative.title:
          newPromiseTotalEnergy = this.proposalConservative.energy;
          break;
        default:
          newPromiseTotalEnergy = Energy.of(this.customEnergy, this.energyUnit);
          break;
      }

      new YieldPromiseStatusApi()
        .forwardToErrorPageOnException()
        .setMinimumDurationMsForCalls(500)
        .updateYieldPromise(
          this.company.getId(),
          this.plant.getId(),
          this.period.getId(),
          { promised_total_wh: newPromiseTotalEnergy.asWh() },
        )
        .then(() => {
          this.period.setPromisedTotal(newPromiseTotalEnergy);
          this.$emit('period-updated', newPromiseTotalEnergy);
        })
        .finally(() => (this.saveOngoing = false));
    },
  },
  computed: {
    saveDisabled(): boolean {
      const proposalTitleEqualsCustomAndInvalid =
        this.selectedProposalTitle === this.customProposalTitle &&
        (this.customEnergy === null || this.customEnergy <= 0);

      return (
        this.saveOngoing ||
        this.selectedProposalTitle === null ||
        proposalTitleEqualsCustomAndInvalid
      );
    },
    proposalsLoading() {
      return this.ltmProposals.length === 0;
    },
    meterCount() {
      return this.meters ? this.meters.length : null;
    },
    sumPeakWatt() {
      return this.meters
        ? this.meters //
            .map((m) => m.getPeakInWatt()) //
            .reduce((a, b) => a + b, 0)
        : 0;
    },
    energyUnit() {
      return this.period.getPromisedTotal().getFormattedUnit();
    },
    proposalConservative() {
      return {
        title: this.$t('features.long_term.step.conservative_title'),
        description: this.$t(
          'features.long_term.step.conservative_description',
        ),
        energy: Energy.zero(),
      };
    },
    proposalOptimistic() {
      return {
        title: this.$t('features.long_term.step.optimistic_title'),
        description: this.$t('features.long_term.step.optimistic_description'),
        energy: Energy.zero(),
      };
    },
    currentSettingTitle() {
      return this.$t('features.long_term.step.current_title');
    },
    currentSettingDescription() {
      return this.$t('features.long_term.step.current_description');
    },
    proposalCurrent() {
      return {
        title: this.currentSettingTitle,
        description: this.currentSettingDescription,
        energy: this.period.getPromisedTotal(),
      };
    },
    customProposalTitle() {
      return this.$t('features.long_term.step.custom_title');
    },
    customProposalDescription() {
      return this.$t('features.long_term.step.custom_description');
    },
  },
  data() {
    return {
      saveOngoing: false,
      meters: null,
      radioName: 'ltm-proposal-radio',
      ltmProposals: [],

      selectedProposalTitle: null as string,
      customEnergy: null,
    };
  },
  mounted() {
    this.customEnergy = parseFloat(
      {
        MWh: this.period.getPromisedTotal().asMwh(),
        kWh: this.period.getPromisedTotal().asKwh(),
        Wh: this.period.getPromisedTotal().asWh(),
      }[this.energyUnit],
    );

    this.selectedProposalTitle = this.currentSettingTitle;

    new PlantApi()
      .getSinglePlantMeters(this.company.getId(), this.plant.getId())
      .then(
        (rsp) =>
          (this.meters = rsp.data
            .map((mJson) => new Meter(mJson))
            .filter((meter) => !meter.isRetired())),
      )
      .then((meters) => {
        const promises = meters.map((meter) =>
          new PrognosisApi()
            .yearlyPrognosis(this.plant, meter)
            .then((rsp) => rsp.data.data),
        );
        Promise.all(promises) //
          .then((results) => {
            this.proposalConservative.energy = Energy.ofWh(
              results
                .map((prog) => prog.p90_wh) //
                .reduce((a, b) => a + b, 0),
            );
            this.proposalOptimistic.energy = Energy.ofWh(
              results
                .map((prog) => prog.p50_wh) //
                .reduce((a, b) => a + b, 0),
            );

            this.ltmProposals = [
              this.proposalCurrent,
              this.proposalConservative,
              this.proposalOptimistic,
            ];
          });
      });
  },
};
</script>

<style scoped></style>
